import React, { useState, useEffect } from "react";
import { adminApi } from "../../api";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sparkles,
  FileText,
  MessageSquare,
  HelpCircle,
  GitCompare,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const ContentPage = () => {
  const [products, setProducts] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [contentType, setContentType] = useState("review");
  const [generatedContent, setGeneratedContent] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [nichesRes, productsRes] = await Promise.all([
          adminApi.getNiches(),
          adminApi.getProducts(),
        ]);
        setNiches(nichesRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleGenerate = async () => {
    if (!selectedProduct) {
      toast.error("Please select a product");
      return;
    }

    setGenerating(true);
    setGeneratedContent("");

    try {
      const res = await adminApi.generateContent({
        product_id: selectedProduct,
        content_type: contentType,
      });
      setGeneratedContent(res.data.generated_content);
      toast.success("Content generated successfully!");
    } catch (error) {
      toast.error("Failed to generate content");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const contentTypes = [
    { value: "review", label: "Full Review", icon: FileText, description: "300-500 word comprehensive review" },
    { value: "verdict", label: "Quick Verdict", icon: MessageSquare, description: "3-4 sentence summary" },
    { value: "faq", label: "FAQ Section", icon: HelpCircle, description: "5 common questions & answers" },
    { value: "comparison", label: "Comparison", icon: GitCompare, description: "Compare with competitors" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="content-loading">
        <div className="animate-pulse text-[#A1A1AA]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">AI Content Engine</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">
          Generate high-converting affiliate content with AI
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls Panel */}
        <div className="lg:col-span-1 space-y-6">
          {/* Product Selection */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
            <h3 className="text-white font-semibold mb-4">Select Product</h3>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/10 text-white rounded-md p-3"
              data-testid="content-product-select"
            >
              <option value="">Choose a product...</option>
              {niches.map((niche) => (
                <optgroup key={niche.id} label={niche.name}>
                  {products
                    .filter((p) => p.niche_id === niche.id)
                    .map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Content Type Selection */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
            <h3 className="text-white font-semibold mb-4">Content Type</h3>
            <div className="space-y-2">
              {contentTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setContentType(type.value)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                      contentType === type.value
                        ? "bg-[#007AFF]/20 border-[#007AFF]/50 text-white"
                        : "bg-[#1A1A1A] border-white/5 text-[#A1A1AA] hover:border-white/20"
                    }`}
                    data-testid={`content-type-${type.value}`}
                  >
                    <Icon size={20} className={contentType === type.value ? "text-[#007AFF]" : ""} />
                    <div>
                      <p className="font-medium">{type.label}</p>
                      <p className="text-xs opacity-70 mt-0.5">{type.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={!selectedProduct || generating}
            className="w-full bg-gradient-to-r from-[#007AFF] to-[#00D4FF] hover:opacity-90 text-white h-12"
            data-testid="generate-content-btn"
          >
            {generating ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={18} className="mr-2" />
                Generate Content
              </>
            )}
          </Button>
        </div>

        {/* Output Panel */}
        <div className="lg:col-span-2">
          <div className="bg-[#121212] border border-white/10 rounded-lg h-full">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-semibold">Generated Content</h3>
              {generatedContent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="border-white/10 text-[#A1A1AA] hover:text-white"
                  data-testid="copy-content-btn"
                >
                  {copied ? (
                    <>
                      <Check size={14} className="mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={14} className="mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="p-5 min-h-[500px]">
              {generating ? (
                <div className="flex flex-col items-center justify-center h-64 text-[#A1A1AA]">
                  <Loader2 size={32} className="animate-spin mb-4" />
                  <p>Generating {contentType} content...</p>
                  <p className="text-sm mt-2">This may take a few seconds</p>
                </div>
              ) : generatedContent ? (
                <div className="prose prose-invert prose-sm max-w-none" data-testid="generated-content">
                  <ReactMarkdown>{generatedContent}</ReactMarkdown>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-[#A1A1AA]">
                  <Sparkles size={48} className="mb-4 opacity-50" />
                  <p>Select a product and content type to generate</p>
                  <p className="text-sm mt-2">AI-powered content will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentPage;
